import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faComments, faStar } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import TicketChatHeader from './componentes/TicketChatHeader';
import TicketCustomerSidebar from './componentes/TicketCustomerSidebar';
import AssignTicketToAgentList from '../TicketsView/componentes/AssignTicketToAgentList';
import { TicketMessagesStream } from './componentes/StreamComponents';
import { SendMessage } from './componentes/SendMessage';
import {
  MergeTicketsModal,
  SelectTicketToMergeModal,
  ClientOpenTicketsBanner,
  TicketCombinedBanner,
  CombinedTicketMessagesModal,
  ClientNotesModal
} from './componentes';
import { normalizeTicketData } from './utils';
import { setTabBaseTitle, clearTabNotification } from '../../utils';
import {
  useTicketDetail,
  useTicketParticipants,
  useTicketMessages,
  useTicketSignalR,
  useTicketAtajos,
  useClientOpenTickets,
  useTicketNotes,
  useClientNotes
} from './hooks';

const TicketDetailView = ({ ticket: propTicket, onBack, onTicketUpdated, onTicketDetailLoaded }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto, idUsuario } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const ticket = normalizeTicketData(propTicket);
  const activeTicketId = ticket?.idTicket || ticket?.id;

  // Estados locales de interfaz para redacción de mensajes y asesores
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [inputText, setInputText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const messageInputRef = useRef(null);

  // 1. Hook para información y metadatos del ticket
  const {
    activeTicket,
    ticketDetail,
    setTicketDetail,
    isLoadingDetail,
    fetchTicketDetail,
    markCurrentAsRead
  } = useTicketDetail({
    ticket,
    token,
    onTicketDetailLoaded
  });

  useEffect(() => {
    if (activeTicketId) {
      const customerName = ticketDetail?.vCustomerName || activeTicket?.vCustomerName || ticket?.vCustomerName || ticket?.vTelefono || 'Cliente';
      setTabBaseTitle(`Ticket #${activeTicketId} · ${customerName} | Akasia Support`);
    }
    return () => {
      clearTabNotification();
    };
  }, [activeTicketId, ticketDetail?.vCustomerName, activeTicket?.vCustomerName, ticket?.vCustomerName, ticket?.vTelefono]);

  const parseDestinoFromMotivo = (motivo) => {
    if (!motivo) return null;
    const match = String(motivo).match(/ticket\s*#?\s*(\d+)/i);
    return match ? Number(match[1]) : null;
  };

  const iEstadoTicket = activeTicket?.iEstadoTicket ?? ticketDetail?.iEstadoTicket ?? ticket?.iEstadoTicket;
  const idTicketDestino = activeTicket?.idTicketDestino
    ?? ticketDetail?.idTicketDestino
    ?? ticket?.idTicketDestino
    ?? parseDestinoFromMotivo(activeTicket?.vMotivoCerrado || ticketDetail?.vMotivoCerrado || ticket?.vMotivoCerrado);
  const bIsCombinado = Boolean(
    activeTicket?.bIsCombinado === 1 ||
    activeTicket?.bIsCombinado === true ||
    ticketDetail?.bIsCombinado === 1 ||
    ticketDetail?.bIsCombinado === true ||
    ticket?.bIsCombinado === 1 ||
    ticket?.bIsCombinado === true ||
    idTicketDestino
  );

  const [isSwappingPrincipal, setIsSwappingPrincipal] = useState(false);

  const handleNavigateToTarget = (targetId) => {
    const idToOpen = targetId || idTicketDestino;
    if (!idToOpen) return;
    window.open(`/support-engine/ticket/${idToOpen}`, '_blank');
  };

  // Cargar lista de agentes para opciones de asignación
  const fetchAgents = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/SupportEngine/GetAgentsList', {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (response.ok) {
        const res = await response.json();
        const data = res?.data || (Array.isArray(res) ? res : []);
        if (Array.isArray(data)) setAgents(data);
      }
    } catch (e) {
      console.warn('Error al cargar lista de asesores:', e);
    }
  }, [token]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Manejador para asignar el ticket (Tomar ticket)
  const handleAssignTicket = async (targetTicket, selectedAgent) => {
    try {
      const target = selectedAgent || {
        idUsuario: Number(idUsuario),
        vNombreCompleto: nombreCompleto || 'Asesor'
      };

      const payload = {
        idTicket: Number(targetTicket?.idTicket || targetTicket?.id || activeTicketId),
        idUsuarioAsignado: Number(target.idUsuario),
        vUpdatedUser: target.vNombreCompleto || nombreCompleto || 'Asesor'
      };

      const response = await fetch('/api/SupportEngine/AssignTicketToAgent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al asignar el ticket');
      }

      if (showSuccessToast) {
        showSuccessToast(
          'Asignación exitosa',
          `Ticket #${payload.idTicket} asignado a ${target.vNombreCompleto}. Saludo enviado.`
        );
      }

      setShowAssignModal(false);
      await fetchTicketDetail(true);
      setHistoryRefreshKey(prev => prev + 1);
      if (onTicketUpdated) onTicketUpdated();
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error de asignación', err.message);
      }
    }
  };

  // 2. Hook para gestión de participantes y colaboradores
  const {
    participants,
    isLoadingParticipants,
    fetchParticipants,
    syncTicketAssignmentAfterMessage
  } = useTicketParticipants({
    activeTicketId,
    activeTicket,
    token,
    idUsuario,
    nombreCompleto,
    setTicketDetail,
    onTicketUpdated
  });

  // 3. Hook para historial de mensajes, scroll infinito y envíos optimistas (texto / media)
  const {
    messages,
    setMessages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    isSending,
    handleLoadMore,
    handleSendMessage
  } = useTicketMessages({
    activeTicketId,
    ticketPhone: activeTicket.phone,
    token,
    idUsuario,
    nombreCompleto,
    inputText,
    setInputText,
    selectedFile,
    setSelectedFile,
    filePreview,
    setFilePreview,
    syncTicketAssignmentAfterMessage,
    setTicketDetail,
    onTicketUpdated,
    showErrorToast
  });

  // 4. Hook para suscripción SignalR en tiempo real
  useTicketSignalR({
    activeTicketId,
    token,
    setMessages,
    markCurrentAsRead,
    fetchTicketDetail,
    onTicketUpdated,
    onRefreshHistory: () => setHistoryRefreshKey(prev => prev + 1),
    ticketDetail,
    propTicket: ticket
  });

  // 4.1. Sincronizar recarga del historial si llega actividad de cliente en un ticket cerrado
  useEffect(() => {
    const handleHistoryRefresh = e => {
      const targetId = Number(e?.detail?.idTicket);
      if (targetId && Number(activeTicketId) === targetId) {
        const currentStatus = Number(ticketDetail?.iEstadoTicket ?? ticket?.iEstadoTicket);
        if (currentStatus === 2) {
          fetchTicketDetail(true);
          setHistoryRefreshKey(prev => prev + 1);
        }
      }
    };
    window.addEventListener('akasia:support:ticket_history_refresh', handleHistoryRefresh);
    return () => {
      window.removeEventListener('akasia:support:ticket_history_refresh', handleHistoryRefresh);
    };
  }, [activeTicketId, ticketDetail, ticket, fetchTicketDetail]);

  // 5. Hook para atajos rápidos de teclado y catálogo
  const {
    quickCommands,
    fetchAtajos,
    handleQuickCommand
  } = useTicketAtajos({
    token,
    idUsuario,
    messageInputRef,
    setInputText,
    onSendMessage: handleSendMessage
  });

  // 6. Detección y gestión de tickets abiertos repetidos del cliente
  const [showSelectMergeModal, setShowSelectMergeModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedExistingTicket, setSelectedExistingTicket] = useState(null);
  const [isMergingTickets, setIsMergingTickets] = useState(false);
  const [showCombinedModal, setShowCombinedModal] = useState(false);
  const [viewingCombinedTicketId, setViewingCombinedTicketId] = useState(null);

  // 6. Hook para notas internas del ticket
  const notesData = useTicketNotes({
    activeTicketId,
    token,
    onNoteChanged: () => {
      setHistoryRefreshKey(prev => prev + 1);
      fetchTicketDetail(true);
    }
  });

  // 6.1 Hook para notas del cliente (propiedad a nivel cliente)
  const clientNotesData = useClientNotes({
    customerId: activeTicket?.idClienteAkasia,
    token
  });
  const [showClientNotesModal, setShowClientNotesModal] = useState(false);
  const [editingClientNote, setEditingClientNote] = useState(null);

  const handleOpenClientNotesModal = (noteToEdit = null) => {
    setEditingClientNote(noteToEdit);
    setShowClientNotesModal(true);
  };

  const handleOpenCombinedMessages = (ticketIdToView) => {
    setViewingCombinedTicketId(ticketIdToView || idTicketDestino);
    setShowCombinedModal(true);
  };

  // 7. Cargar tickets combinados absorbidos ÚNICAMENTE si este ticket es el Principal
  const [combinedSourceTickets, setCombinedSourceTickets] = useState([]);

  useEffect(() => {
    // Si este ticket es el secundario/combinado, NUNCA debe mostrar el historial combinado
    if (bIsCombinado || !activeTicketId || !token) {
      setCombinedSourceTickets([]);
      return;
    }

    let isMounted = true;
    const fetchCombinedSourceTickets = async () => {
      try {
        const resp = await fetch(`/api/SupportEngine/GetTicketEvents?idTicket=${activeTicketId}`, {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + token
          }
        });
        if (resp.ok) {
          const res = await resp.json();
          const list = res.data || res;
          if (Array.isArray(list) && isMounted) {
            const combinedTickets = [];
            list.forEach(ev => {
              if (ev.vTipoEvento === 'COMBINACION_TICKETS' && ev.vDescripcion) {
                const match = ev.vDescripcion.match(/#(\d+)/);
                if (match) {
                  const sourceId = Number(match[1]);
                  if (sourceId !== Number(activeTicketId) && !combinedTickets.some(t => t.idTicket === sourceId)) {
                    combinedTickets.push({
                      idTicket: sourceId,
                      fecha: ev.dCreatedDate,
                      descripcion: ev.vDescripcion
                    });
                  }
                }
              }
            });
            setCombinedSourceTickets(combinedTickets);
          }
        }
      } catch (err) {
        console.warn('Error al obtener historial de combinación del ticket principal:', err);
      }
    };

    fetchCombinedSourceTickets();

    return () => {
      isMounted = false;
    };
  }, [activeTicketId, bIsCombinado, token, historyRefreshKey]);

  const {
    openTickets: clientOpenTickets,
    fetchClientOpenTickets
  } = useClientOpenTickets({
    idClienteAkasia: activeTicket?.idClienteAkasia,
    activeTicketId,
    token,
    iEstadoTicket
  });

  // Sincronizar recarga del detalle del ticket si llega actividad de otro número o ticket del mismo cliente
  useEffect(() => {
    const handleClientActivity = e => {
      const eventClientId = Number(e?.detail?.idClienteAkasia);
      const currentClientId = Number(activeTicket?.idClienteAkasia || ticketDetail?.idClienteAkasia);
      if (currentClientId && eventClientId === currentClientId) {
        fetchTicketDetail(true);
      }
    };
    window.addEventListener('akasia:support:ticket_activity', handleClientActivity);
    return () => {
      window.removeEventListener('akasia:support:ticket_activity', handleClientActivity);
    };
  }, [activeTicket?.idClienteAkasia, ticketDetail?.idClienteAkasia, fetchTicketDetail]);

  const handleSwapPrincipalTicket = async () => {
    const currentId = activeTicket?.idTicket || activeTicketId;
    const targetTicketId = idTicketDestino;
    if (!currentId || !targetTicketId || isSwappingPrincipal) return;

    setIsSwappingPrincipal(true);
    try {
      const resp = await fetch('/api/SupportEngine/SwapMajorTicket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicketCombinado: Number(currentId),
          idTicketPrincipal: Number(targetTicketId)
        })
      });

      const data = await resp.json().catch(() => ({}));
      if (data?.iResponseType === 1 || resp.ok) {
        if (showSuccessToast) {
          showSuccessToast('Ticket Principal Actualizado', `El ticket #${currentId} ahora es el ticket principal.`);
        }
        await fetchTicketDetail(true);
        if (fetchClientOpenTickets) {
          await fetchClientOpenTickets();
        }
        setHistoryRefreshKey(prev => prev + 1);
        if (onTicketUpdated) onTicketUpdated();
      } else {
        if (showErrorToast) {
          showErrorToast('Error', data?.vErrorDescription || 'No se pudo cambiar el ticket principal');
        }
      }
    } catch (err) {
      console.error('Error al intercambiar ticket principal:', err);
      if (showErrorToast) {
        showErrorToast('Error', 'Ocurrió un error al actualizar el ticket principal');
      }
    } finally {
      setIsSwappingPrincipal(false);
    }
  };

  const handleOpenMergeFlow = () => {
    if (!clientOpenTickets || clientOpenTickets.length === 0) return;
    if (clientOpenTickets.length === 1) {
      setSelectedExistingTicket(clientOpenTickets[0]);
      setShowMergeModal(true);
    } else {
      setShowSelectMergeModal(true);
    }
  };

  const handleSelectCandidateTicket = ticketItem => {
    setSelectedExistingTicket(ticketItem);
    setShowSelectMergeModal(false);
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (!selectedExistingTicket || isMergingTickets) return;

    const sourceId = Number(activeTicketId);
    const targetId = Number(selectedExistingTicket.idTicket || selectedExistingTicket.id);
    const sourcePhone = activeTicket?.phone || activeTicket?.ticketPhone || activeTicket?.vTelefono;
    const targetPhone = selectedExistingTicket?.phone || selectedExistingTicket?.ticketPhone || selectedExistingTicket?.vTelefono || sourcePhone;
    setIsMergingTickets(true);

    try {
      const resp = await fetch('/api/SupportEngine/MergeTickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicketSource: sourceId,
          idTicketTarget: targetId,
          idUsuario: idUsuario ? Number(idUsuario) : null,
          agentUser: nombreCompleto || null
        })
      });

      if (resp.ok) {
        setShowMergeModal(false);
        setSelectedExistingTicket(null);
        showSuccessToast(`Ticket #${sourceId} combinado con éxito en Ticket #${targetId}.`);
        setTicketDetail(prev => ({
          ...(prev || {}),
          iEstadoTicket: 4,
          idTicketDestino: targetId
        }));

        // 1. Agregar nota interna a ambos tickets
        try {
          await fetch('/api/SupportEngine/CreateTicketNote', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
              ticketId: sourceId,
              note: `Este ticket ha sido combinado con el ticket #${targetId}. Ticket principal ticket #${targetId}`,
              noteColor: '#dbeafe'
            })
          });

          await fetch('/api/SupportEngine/CreateTicketNote', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
              ticketId: targetId,
              note: `Se ha combinado el ticket #${sourceId} con este ticket. Ticket principal ticket #${targetId}`,
              noteColor: '#dbeafe'
            })
          });

          await notesData.fetchNotes();
        } catch (noteErr) {
          console.warn('No se pudieron crear las notas de combinación:', noteErr);
        }

        // 2. Enviar mensaje automático por WhatsApp a ambos tickets notificando la combinación
        try {
          if (sourcePhone) {
            await fetch('/api/SupportEngine/SendTicketMessage', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
              },
              body: JSON.stringify({
                idTicket: sourceId,
                toPhoneNumber: sourcePhone,
                message: `Este ticket ha sido combinado con el ticket #${targetId}. Ticket principal ticket #${targetId}`,
                idUsuario: idUsuario ? Number(idUsuario) : null
              })
            });
          }

          if (targetPhone) {
            await fetch('/api/SupportEngine/SendTicketMessage', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
              },
              body: JSON.stringify({
                idTicket: targetId,
                toPhoneNumber: targetPhone,
                message: `El ticket #${sourceId} ha sido combinado con este ticket. Ticket principal ticket #${targetId}`,
                idUsuario: idUsuario ? Number(idUsuario) : null
              })
            });
          }
        } catch (msgErr) {
          console.warn('No se pudieron enviar los mensajes automáticos de combinación:', msgErr);
        }

        await fetchTicketDetail(true);
        await fetchClientOpenTickets();
        setHistoryRefreshKey(prev => prev + 1);
        if (onTicketUpdated) onTicketUpdated();
      } else {
        const errData = await resp.json().catch(() => ({}));
        showErrorToast(errData.message || 'Error al combinar tickets.');
      }
    } catch (err) {
      console.error('Error al combinar tickets:', err);
      showErrorToast('Error de conexión al combinar tickets.');
    } finally {
      setIsMergingTickets(false);
    }
  };

  // Comandos incluyendo el atajo programado para preguntar por combinación si existen tickets abiertos
  const allCommands = useMemo(() => {
    if (!clientOpenTickets || clientOpenTickets.length === 0) return quickCommands;
    const firstTicket = clientOpenTickets[0];
    const firstTicketId = firstTicket?.idTicket || firstTicket?.id;
    const firstSubject = firstTicket?.vAsunto ? ` respecto a "${firstTicket.vAsunto}"` : '';

    const dynamicMergeCmd = {
      vComando: `/combinar`,
      vTitulo: `Preguntar al cliente si desea combinar con Ticket #${firstTicketId}`,
      vTexto: clientOpenTickets.length === 1
        ? `Hola, notamos que ya cuentas con el ticket #${firstTicketId} abierto en atención${firstSubject}. ¿Deseas que combinemos esta conversación con tu ticket existente para darte seguimiento conjunto en un solo reporte?`
        : `Hola, identificamos que cuentas con tickets abiertos en atención. ¿Deseas que unifiquemos esta conversación con tu ticket previo para darte seguimiento continuo en un solo reporte?`
    };

    return [dynamicMergeCmd, ...quickCommands];
  }, [quickCommands, clientOpenTickets]);

  return (
    <div
      className="d-flex flex-column flex-xl-row border rounded-3 overflow-hidden shadow-none"
      style={{
        height: 'calc(100vh - 120px)',
        minHeight: '550px',
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {/* 1. Left / Center Chat Column */}
      <div
        id="ticket-chat-main-column"
        className="flex-grow-1 d-flex flex-column border-end h-100 position-relative overflow-hidden"
        style={{
          backgroundColor: isDark ? '#0b141a' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0',
          minWidth: 0
        }}
      >
        {/* Header Component */}
        <div className="flex-shrink-0">
          <TicketChatHeader
            ticket={activeTicket}
            onBack={onBack}
            onOpenAssignModal={() => setShowAssignModal(true)}
            onTicketClosed={async () => {
              await fetchTicketDetail(true);
              setHistoryRefreshKey(prev => prev + 1);
              if (onTicketUpdated) onTicketUpdated();
            }}
            onTicketUpdated={async () => {
              await fetchTicketDetail(true);
            }}
            clientNotes={clientNotesData.notes}
            onOpenClientNotesModal={handleOpenClientNotesModal}
          />
        </div>

        {/* Banner fijo de Tomar Ticket cuando el estado es Pendiente / Sin Asignar (iEstadoTicket === 1) */}
        {Number(iEstadoTicket) === 1 && (
          <div className="flex-shrink-0 p-3 pb-0">
            <AssignTicketToAgentList
              inline
              ticket={activeTicket}
              agents={agents}
              currentUserId={idUsuario}
              currentUserName={nombreCompleto}
              onConfirm={handleAssignTicket}
            />
          </div>
        )}

        {/* Banner en el Ticket Principal: Visualizar conversación de tickets combinados en este ticket */}
        {!bIsCombinado && combinedSourceTickets.length > 0 && (
          <div
            className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 flex-shrink-0"
            style={{
              backgroundColor: isDark ? 'rgba(168, 85, 247, 0.12)' : '#faf5ff',
              borderColor: isDark ? 'rgba(168, 85, 247, 0.25)' : '#e9d5ff'
            }}
          >
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                className="badge d-flex align-items-center gap-1 py-1 px-2"
                style={{
                  backgroundColor: isDark ? 'rgba(168, 85, 247, 0.25)' : '#f3e8ff',
                  color: isDark ? '#c084fc' : '#7e22ce',
                  border: isDark ? '1px solid rgba(192, 132, 252, 0.3)' : '1px solid #d8b4fe',
                  fontSize: '0.74rem'
                }}
              >
                <FontAwesomeIcon icon={faExchangeAlt} />
                Conversación Combinada
              </span>
              <span style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                Se combinó historial previo en este ticket principal:
              </span>
              {combinedSourceTickets.map(item => (
                <button
                  key={item.idTicket}
                  type="button"
                  onClick={() => handleOpenCombinedMessages(item.idTicket)}
                  className="btn btn-sm shadow-none d-inline-flex align-items-center gap-1 py-0 px-2 fw-semibold"
                  style={{
                    backgroundColor: isDark ? '#1e293b' : '#ffffff',
                    borderColor: isDark ? '#a855f7' : '#c084fc',
                    color: isDark ? '#d8b4fe' : '#6b21a8',
                    border: '1px solid',
                    borderRadius: '6px',
                    fontSize: '0.76rem',
                    cursor: 'pointer'
                  }}
                  title={`Ver la conversación transferida del Ticket #${item.idTicket}`}
                >
                  <FontAwesomeIcon icon={faComments} style={{ fontSize: '0.70rem' }} />
                  Ver conversación Ticket #{item.idTicket}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Banner de detección de tickets abiertos repetidos del cliente */}
        {clientOpenTickets.length > 0 && !bIsCombinado && (
          <div className="flex-shrink-0">
            <ClientOpenTicketsBanner
              openTickets={clientOpenTickets}
              onOpenMergeModal={handleOpenMergeFlow}
              isMerging={isMergingTickets}
            />
          </div>
        )}

        {/* Banner de ticket combinado con enlace directo al ticket destino */}
        {bIsCombinado && Boolean(idTicketDestino) && (
          <div className="flex-shrink-0">
            <TicketCombinedBanner
              idTicketDestino={idTicketDestino}
              ticket={activeTicket}
              onNavigateToTarget={handleNavigateToTarget}
              onSwapPrincipal={handleSwapPrincipalTicket}
              isSwapping={isSwappingPrincipal}
            />
          </div>
        )}

        {/* Messages Stream Component (Scroll vertical único) */}
        <TicketMessagesStream
          messages={messages}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          error={error}
        />

        {/* Message Composer Component / Aviso de Asignación / Aviso de Cerrado */}
        {Number(iEstadoTicket) === 1 ? (
          <div
            className="p-3 border-top flex-shrink-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
            style={{
              backgroundColor: isDark ? '#121e2d' : '#f8fafc',
              borderColor: isDark ? '#233142' : '#e2e8f0'
            }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.86rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              <span>Este ticket se encuentra pendiente de asignación. Asigna un asesor para responder al cliente.</span>
            </div>
            <button
              type="button"
              onClick={() => setShowAssignModal(true)}
              className="btn btn-sm text-white d-flex align-items-center gap-2 px-3 py-1.5 rounded-2 shadow-none"
              style={{
                backgroundColor: '#059669',
                fontSize: '0.85rem',
                fontWeight: 600,
                border: 'none'
              }}
            >
              <span>Asignar Ticket</span>
            </button>
          </div>
        ) : Number(iEstadoTicket) === 2 ? (
          <div
            className="p-3 text-center border-top flex-shrink-0"
            style={{
              backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : '#fef2f2',
              borderColor: isDark ? '#233142' : '#fee2e2',
              color: isDark ? '#f87171' : '#dc2626',
              fontSize: '0.85rem'
            }}
          >
            <strong>Este ticket está cerrado.</strong>
          </div>
        ) : (
          <div
            className="flex-shrink-0 p-2 p-md-3"
            style={{
              backgroundColor: isDark ? '#0b141a' : '#f8fafc',
              borderTop: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
            }}
          >
            <SendMessage
              inputRef={messageInputRef}
              inputText={inputText}
              setInputText={setInputText}
              onSendMessage={handleSendMessage}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              filePreview={filePreview}
              setFilePreview={setFilePreview}
              isSending={isSending}
              commands={allCommands}
              quickCommands={quickCommands}
              onSelectCommand={handleQuickCommand}
              onReloadAtajos={fetchAtajos}
              clientOpenTickets={clientOpenTickets}
              onOpenMergeModal={handleOpenMergeFlow}
              ticket={activeTicket}
              onCreateTicketNote={notesData.createNote}
              onUpdateTicketNote={notesData.updateNote}
              isSavingNote={notesData.isSavingNote}
            />
          </div>
        )}
      </div>

      {/* 2. Right Details Sidebar Component */}
      <TicketCustomerSidebar
        ticket={activeTicket}
        isLoadingDetail={isLoadingDetail}
        participants={participants}
        isLoadingParticipants={isLoadingParticipants}
        onReloadParticipants={fetchParticipants}
        historyRefreshKey={historyRefreshKey}
        onOpenCombinedMessages={handleOpenCombinedMessages}
        notes={notesData.notes}
        isLoadingNotes={notesData.isLoadingNotes}
        isSavingNote={notesData.isSavingNote}
        createNote={notesData.createNote}
        updateNote={notesData.updateNote}
        deleteNote={notesData.deleteNote}
        onTicketUpdated={async () => {
          await fetchTicketDetail(true);
          await fetchClientOpenTickets();
          setHistoryRefreshKey(prev => prev + 1);
          if (onTicketUpdated) onTicketUpdated();
        }}
      />

      {/* Modal para seleccionar cuál ticket combinar si hay múltiples */}
      <SelectTicketToMergeModal
        show={showSelectMergeModal}
        onClose={() => setShowSelectMergeModal(false)}
        client={{
          idClienteAkasia: activeTicket?.idClienteAkasia,
          vNombreCompleto: activeTicket?.customerName
        }}
        currentTicket={activeTicket}
        openTickets={clientOpenTickets}
        onSelectTicket={handleSelectCandidateTicket}
      />

      {/* Modal cara a cara para confirmar combinación */}
      <MergeTicketsModal
        show={showMergeModal}
        onClose={() => {
          setShowMergeModal(false);
          setSelectedExistingTicket(null);
        }}
        client={{
          idClienteAkasia: activeTicket?.idClienteAkasia,
          vNombreCompleto: activeTicket?.customerName
        }}
        currentTicket={activeTicket}
        existingTicket={selectedExistingTicket}
        onConfirmMerge={handleConfirmMerge}
        isProcessing={isMergingTickets}
      />

      {/* Modal para visualizar los mensajes del ticket combinado */}
      <CombinedTicketMessagesModal
        show={showCombinedModal}
        onClose={() => {
          setShowCombinedModal(false);
          setViewingCombinedTicketId(null);
        }}
        idTicket={viewingCombinedTicketId || idTicketDestino}
        token={token}
      />

      {/* MODAL DE ASIGNACIÓN DE ASESOR */}
      <AssignTicketToAgentList
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        ticket={activeTicket}
        agents={agents}
        currentUserId={idUsuario}
        currentUserName={nombreCompleto}
        onConfirm={handleAssignTicket}
      />

      {/* Modal para visualizar y gestionar notas del cliente */}
      <ClientNotesModal
        show={showClientNotesModal}
        onClose={() => {
          setShowClientNotesModal(false);
          setEditingClientNote(null);
        }}
        customerId={activeTicket?.idClienteAkasia}
        customerName={activeTicket?.customerName}
        notes={clientNotesData.notes}
        isLoadingNotes={clientNotesData.isLoadingNotes}
        isSavingNote={clientNotesData.isSavingNote}
        initialEditingNote={editingClientNote}
        createNote={clientNotesData.createNote}
        updateNote={clientNotesData.updateNote}
        deleteNote={clientNotesData.deleteNote}
      />
    </div>
  );
};

export default TicketDetailView;
